import api from './api';
import { ChatMessage, ChatUser, ChatAttachment } from '../types/chat';

class ChatService {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http:///localhost:3000';
  }

  /**
   * Get chat messages for a specific chat
   */
  async getMessages(chatId: string): Promise<ChatMessage[]> {
    try {
      const response = await api.get('/chat/messages', {
        params: {
          page: 1,
          limit: 100 // Get more messages to ensure we have enough for the chat
        }
      });
      
      // Filter messages to ensure they belong to the current chat
      const messages = response.data.messages.filter((msg: ChatMessage) => {
        if (msg.messageType === 'broadcast') {
          return chatId === 'broadcast';
        }
        // For direct messages, check if the message is between the current user and the chat partner
        return (msg.senderId === chatId || msg.recipientId === chatId);
      });

      return messages;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  /**
   * Send a new message
   */
  async sendMessage(
    content: string,
    type: 'direct' | 'broadcast',
    recipientId?: string,
    attachments?: Array<{ type: 'image' | 'file'; url: string; name: string }>
  ): Promise<ChatMessage> {
    try {
      const response = await api.post('/chat/messages', {
        content,
        messageType: type,
        recipientId,
        attachments
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Get all users in the company
   */
  async getUsers(): Promise<ChatUser[]> {
    try {
      const response = await api.get('/chat/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Update user status
   */
  async updateStatus(status: 'online' | 'offline' | 'away'): Promise<void> {
    try {
      await api.post('/chat/status', { status });
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  }

  /**
   * Mark messages as read
   */
  async markMessagesAsRead(chatId: string): Promise<void> {
    await api.post(`/chat/messages/${chatId}/read`);
  }
}

export const chatService = new ChatService(); 